import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading } from './indicators/Loading'
import type { ReactNode } from 'react'
import { useAuth } from '@/lib/auth/useAuth'
import { hasUsableAccessToken, refreshAccessToken } from '@/lib/auth/tokenRefresh'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

// Don't render with a JWT that's about to lapse; mint a new one first.
const USABLE_MARGIN_MS = 30_000

type GateStatus = 'checking' | 'ready' | 'expired'

type AuthRefreshGateProps = {
  children: ReactNode
}

/**
 * Blocks the app shell until there is a usable API token.
 *
 * The Better Auth session (60 days, sliding) is what keeps the user signed in;
 * the API JWT is only 15 minutes. After a cold load the stored JWT is often
 * stale, so this mints a fresh one before the shell's first API calls.
 *
 * - Fresh JWT already stored → render immediately.
 * - Refresh succeeds → render.
 * - Auth server unreachable (offline, timeout, 5xx) → render anyway with
 *   whatever token we have: the app is offline-first and RxDB screens work
 *   without the network. API calls retry the refresh on their own 401s.
 * - Session rejected (401) → show a short notice; AuthProvider's
 *   `onSessionExpired` handler runs the logout path and redirects to /auth.
 *
 * No session at all → the route guards (`beforeLoad`) redirect to /auth, so
 * this only shows the spinner for that instant.
 *
 * Proactive refresh while the app is open is not done here: it's
 * tokenRefresh.ts's scheduler, started by AuthProvider.
 */
export function AuthRefreshGate({ children }: AuthRefreshGateProps) {
  const { t } = useTranslation('auth')
  const { isAuthenticated } = useAuth()
  const [status, setStatus] = useState<GateStatus>(() =>
    hasUsableAccessToken(USABLE_MARGIN_MS) ? 'ready' : 'checking',
  )

  useEffect(() => {
    if (status !== 'checking' || !isAuthenticated) return
    let cancelled = false

    logger.logEvent(LogCategory.AUTH_SESSION, 'refresh_gate.attempting_refresh')
    void refreshAccessToken().then((outcome) => {
      if (cancelled) return
      logger.logEvent(LogCategory.AUTH_SESSION, 'refresh_gate.refresh_outcome', { outcome })
      if (outcome === 'unauthorized') {
        // AuthProvider's onSessionExpired handler logs out and redirects.
        setStatus('expired')
        return
      }
      if (outcome === 'no-session') {
        // Signed out (e.g. in another tab) between the guard and now.
        window.location.href = '/auth'
        return
      }
      // ok, or unavailable (hold the session: offline-first), or superseded
      // by a newer sign-in that already has its own token.
      setStatus('ready')
    })

    return () => {
      cancelled = true
    }
  }, [status, isAuthenticated])

  if (status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-sm text-muted-foreground text-center">
        {t('session.expired')}
      </div>
    )
  }

  if (status === 'ready' && isAuthenticated) {
    return <>{children}</>
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loading />
    </div>
  )
}
